import { useEffect, useRef, useState } from "react";
import { Form } from "react-bootstrap";
import styles from "../../../styles/FormStepRenderer.module.css";
import {
    countryCodes,
    findCountryByPhoneNumber,
    formatPhoneNumber,
    getDefaultCountry,
    getFlagUrl,
    validatePhoneNumber
} from "../../../utils/phoneUtils";

const PhoneNumberInput = ({ value, onChange, validation, inputRef, theme, placeholder, disabled, formStyle = "typeform" }) => {
    const [selectedCountry, setSelectedCountry] = useState(getDefaultCountry());
    const [phoneNumber, setPhoneNumber] = useState("");
    const [isValid, setIsValid] = useState(true);
    const [errorMessage, setErrorMessage] = useState("");
    const [showDropdown, setShowDropdown] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const dropdownRef = useRef(null);
    const countryButtonRef = useRef(null);
    const onChangeRef = useRef(onChange);
    const lastValueRef = useRef("");

    // Keep onChange ref up to date
    useEffect(() => {
        onChangeRef.current = onChange;
    }, [onChange]);

    // Initialize from value if provided
    useEffect(() => {
        if (value) {
            const country = findCountryByPhoneNumber(value);
            if (country) {
                setSelectedCountry(country);
                // Extract local number (remove country code)
                const localNumber = value.replace(`+${country.dialCode}`, "").trim();
                setPhoneNumber(localNumber);
            } else {
                // If value doesn't have country code, use default and set as local number
                setPhoneNumber(value.replace(/^\+/, "").replace(/^0+/, ""));
            }
        }
    }, [value]);

    // Validate and update parent when phone number or country changes
    useEffect(() => {
        if (!phoneNumber) {
            setIsValid(true);
            setErrorMessage("");
            const emptyValue = "";
            if (lastValueRef.current !== emptyValue) {
                lastValueRef.current = emptyValue;
                onChangeRef.current(emptyValue);
            }
            return;
        }

        const fullNumber = formatPhoneNumber(phoneNumber, selectedCountry);
        const validationResult = validatePhoneNumber(fullNumber, selectedCountry);
        
        setIsValid(validationResult.valid);
        setErrorMessage(validationResult.error || "");
        
        // Only update parent if the value has actually changed
        if (lastValueRef.current !== fullNumber) {
            lastValueRef.current = fullNumber;
            onChangeRef.current(fullNumber);
        }
    }, [phoneNumber, selectedCountry]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target) &&
                countryButtonRef.current &&
                !countryButtonRef.current.contains(event.target)
            ) {
                setShowDropdown(false);
                setSearchQuery("");
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const handlePhoneNumberChange = (e) => {
        const input = e.target.value;
        // Only allow digits, spaces, dashes, and parentheses for formatting
        if (/^[0-9\s\-()]*$/.test(input) || input === "") {
            setPhoneNumber(input);
        }
    };

    const handleCountrySelect = (country) => {
        setSelectedCountry(country);
        setShowDropdown(false);
        setSearchQuery("");
    };

    // Filter countries based on search query
    const filteredCountries = countryCodes.filter(country =>
        country.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        country.code.includes(searchQuery) ||
        country.dialCode.includes(searchQuery)
    );

    const handleKeyDown = (e) => {
        // Allow digits, spaces, backspace, tab, delete, arrows
        if (
            /^[0-9\s\-()]$/.test(e.key) ||
            e.key === "Backspace" ||
            e.key === "Tab" ||
            e.key === "Delete" ||
            e.key.startsWith("Arrow") ||
            e.key === "Home" ||
            e.key === "End"
        ) {
            return;
        }
        e.preventDefault();
    };

    const displayValue = phoneNumber;
    const isSinglePage = formStyle === "singlePage";
    const containerClass = styles.phoneInputContainer;
    const wrapperClass = styles.phoneInputWrapper;

    // Get theme-based styles for single page - matching name/surname field size exactly
    const getSinglePageButtonStyle = () => {
        if (!isSinglePage) return {};
        return {
            border: `1.5px solid ${theme?.inputBorderColor || "#d1d5db"}`,
            borderRadius: theme?.inputBorderRadius || "8px",
            padding: "0.75rem 1rem",
            backgroundColor: theme?.inputBackground || "#ffffff",
            color: theme?.cardTextColor || "#3f3f3f",
            transition: "all 0.2s ease",
            boxShadow: `0 1px 3px rgba(0, 0, 0, ${theme?.inputShadowIntensity || 0.05})`,
            fontSize: "1rem",
            lineHeight: "1.5",
            fontFamily: "inherit",
            boxSizing: "border-box",
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
            whiteSpace: "nowrap",
            height: "auto",
            minHeight: "unset",
            maxHeight: "none",
        };
    };

    const getSinglePageInputStyle = () => {
        if (!isSinglePage) return {};
        return {
            flex: 1,
            padding: "0.75rem 1rem",
            fontSize: "1rem",
            lineHeight: "1.5",
            color: theme?.inputTextColor || "#1f2937",
            backgroundColor: theme?.inputBackground || "#ffffff",
            border: `1.5px solid ${!isValid ? "#dc3545" : (theme?.inputBorderColor || "#d1d5db")}`,
            borderRadius: theme?.inputBorderRadius || "8px",
            transition: "all 0.2s ease",
            fontFamily: "inherit",
            boxSizing: "border-box",
            width: "100%",
            textAlign: "left",
            margin: 0,
            maxWidth: "none",
            boxShadow: `0 1px 3px rgba(0, 0, 0, ${theme?.inputShadowIntensity || 0.05})`,
            height: "auto",
            minHeight: "unset",
            maxHeight: "none",
        };
    };

    return (
        <div className={containerClass} style={isSinglePage ? { width: "100%", display: "flex", flexDirection: "column", gap: "0.5rem", textAlign: "left", alignItems: "stretch" } : {}}>
            <div className={wrapperClass} style={isSinglePage ? { display: "flex", alignItems: "stretch", width: "100%", gap: "0.5rem", textAlign: "left", justifyContent: "flex-start", maxWidth: "none" } : {}}>
                {/* Country Code Selector */}
                <div className={styles.countrySelectorWrapper} style={isSinglePage ? { flexShrink: 0 } : {}}>
                    <button
                        ref={countryButtonRef}
                        type="button"
                        className={styles.countrySelectorButton}
                        onClick={() => !disabled && setShowDropdown(!showDropdown)}
                        disabled={disabled}
                        style={{
                            ...(isSinglePage ? getSinglePageButtonStyle() : {
                                backgroundColor: theme?.cardBackgroundColor || "#d9d9d9",
                                color: theme?.cardTextColor || "#3f3f3f",
                            }),
                            opacity: disabled ? 0.6 : 1,
                            cursor: disabled ? 'not-allowed' : 'pointer',
                            ...(isSinglePage && {
                                minHeight: "unset",
                                height: "auto",
                            }),
                        }}
                        onFocus={(e) => {
                            if (isSinglePage) {
                                e.target.style.borderColor = theme?.inputFocusBorderColor || "#348C9E";
                                e.target.style.boxShadow = `0 0 0 4px rgba(52, 140, 158, 0.1), 0 4px 12px rgba(52, 140, 158, 0.15)`;
                            }
                        }}
                        onBlur={(e) => {
                            if (isSinglePage) {
                                e.target.style.borderColor = theme?.inputBorderColor || "#e5e7eb";
                                e.target.style.boxShadow = `0 1px 3px rgba(0, 0, 0, ${theme?.inputShadowIntensity || 0.05})`;
                            }
                        }}
                        onMouseEnter={(e) => {
                            if (isSinglePage && document.activeElement !== e.target) {
                                e.target.style.borderColor = theme?.inputHoverBorderColor || "#d1d5db";
                            }
                        }}
                        onMouseLeave={(e) => {
                            if (isSinglePage && document.activeElement !== e.target) {
                                e.target.style.borderColor = theme?.inputBorderColor || "#e5e7eb";
                            }
                        }}
                    >
                        <img 
                            src={getFlagUrl(selectedCountry.flagCode)}
                            alt={selectedCountry.name}
                            className={styles.flagImage}
                            onError={(e) => {
                                // Fallback to a simple div if image fails to load
                                e.target.style.display = 'none';
                            }}
                        />
                        <span className={styles.countryCode}>{selectedCountry.code}</span>
                        <span className={styles.dropdownArrow}>▼</span>
                    </button>
                    
                    {showDropdown && (
                        <div 
                            ref={dropdownRef}
                            className={styles.countryDropdown}
                            style={{
                                backgroundColor: theme?.cardBackgroundColor || "#ffffff",
                                borderColor: theme?.cardTextColor || "#3f3f3f",
                            }}
                        >
                            <div className={styles.countryDropdownSearch}>
                                <input
                                    type="text"
                                    placeholder="Search country..."
                                    className={styles.countrySearchInput}
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    onFocus={(e) => e.stopPropagation()}
                                    autoFocus
                                />
                            </div>
                            <div className={styles.countryDropdownList}>
                                {filteredCountries.length === 0 ? (
                                    <div className={styles.countryOption} style={{ cursor: "default", color: "#999" }}>
                                        No countries found
                                    </div>
                                ) : (
                                    filteredCountries.map((country, index) => (
                                        <button
                                            key={`${country.dialCode}-${index}`}
                                            type="button"
                                            className={`${styles.countryOption} ${
                                                selectedCountry.dialCode === country.dialCode 
                                                    ? styles.countryOptionSelected 
                                                    : ""
                                            }`}
                                            onClick={() => handleCountrySelect(country)}
                                            style={{
                                                backgroundColor: 
                                                    selectedCountry.dialCode === country.dialCode
                                                        ? theme?.buttonColor || "#007bff"
                                                        : "transparent",
                                                color: 
                                                    selectedCountry.dialCode === country.dialCode
                                                        ? theme?.buttonTextColor || "#ffffff"
                                                        : theme?.cardTextColor || "#3f3f3f",
                                            }}
                                        >
                                            <img 
                                                src={getFlagUrl(country.flagCode)}
                                                alt={country.name}
                                                className={styles.flagImage}
                                                onError={(e) => {
                                                    e.target.style.display = 'none';
                                                }}
                                            />
                                            <span className={styles.countryName}>{country.name}</span>
                                            <span className={styles.countryCode}>{country.code}</span>
                                        </button>
                                    ))
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {/* Phone Number Input */}
                <Form.Control
                    ref={(el) => {
                        if (inputRef) {
                            if (typeof inputRef === 'function') {
                                inputRef(el);
                            } else {
                                inputRef.current = el;
                            }
                        }
                        // Set custom validity for HTML5 validation
                        if (el) {
                            if (!isValid && errorMessage) {
                                el.setCustomValidity(errorMessage);
                            } else {
                                el.setCustomValidity("");
                            }
                        }
                    }}
                    type="tel"
                    inputMode="numeric"
                    value={displayValue}
                    onChange={handlePhoneNumberChange}
                    onKeyDown={handleKeyDown}
                    className={`${styles.textInput} ${styles.phoneNumberInput} ${
                        !isValid ? styles.phoneInputInvalid : ""
                    }`}
                    placeholder={placeholder || "Phone number"}
                    required={validation?.required}
                    disabled={disabled}
                    title={errorMessage || validation?.message || ""}
                    style={{
                        ...(isSinglePage ? getSinglePageInputStyle() : {
                            borderColor: !isValid ? "#dc3545" : undefined,
                        }),
                    }}
                    onFocus={(e) => {
                        if (isSinglePage) {
                            e.target.style.borderColor = theme?.inputFocusBorderColor || "#348C9E";
                            e.target.style.boxShadow = `0 0 0 3px rgba(52, 140, 158, 0.1), 0 4px 12px rgba(52, 140, 158, 0.15)`;
                        }
                    }}
                    onBlur={(e) => {
                        if (isSinglePage) {
                            e.target.style.borderColor = !isValid ? "#dc3545" : (theme?.inputBorderColor || "#e5e7eb");
                            e.target.style.boxShadow = `0 1px 3px rgba(0, 0, 0, ${theme?.inputShadowIntensity || 0.05})`;
                        }
                    }}
                    onMouseEnter={(e) => {
                        if (isSinglePage && document.activeElement !== e.target) {
                            e.target.style.borderColor = theme?.inputHoverBorderColor || "#d1d5db";
                        }
                    }}
                    onMouseLeave={(e) => {
                        if (isSinglePage && document.activeElement !== e.target) {
                            e.target.style.borderColor = !isValid ? "#dc3545" : (theme?.inputBorderColor || "#e5e7eb");
                        }
                    }}
                />
            </div>
            
            {/* Validation Message - only show errors in single page view, centered */}
            {isSinglePage && !isValid && errorMessage && (
                <small className={styles.validationMessage} style={{ color: "#dc3545", textAlign: "center", display: "block", marginTop: "0.25rem" }}>
                    {errorMessage}
                </small>
            )}
            {!isSinglePage && !isValid && errorMessage && (
                <small className={styles.validationMessage} style={{ color: "#dc3545" }}>
                    {errorMessage}
                </small>
            )}
            {!isSinglePage && validation?.message && isValid && (
                <small className={styles.validationMessage}>
                    {validation.message}
                </small>
            )}
        </div>
    );
};

export default PhoneNumberInput;

